import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  FileText, 
  FilePlus, 
  Pencil,
  Upload,
  Clock
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useState, useEffect } from "react";

const Index = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState<string>("");

  useEffect(() => {
    const getUserName = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.user_metadata?.name) {
        setUserName(user.user_metadata.name);
      }
    };
    getUserName();
  }, []);

  const { data: recentDocuments } = useQuery({
    queryKey: ['recent-documents'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      
      const { data } = await supabase
        .from('documents')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .limit(5);
      
      return data || [];
    },
  });

  const { data: recentTemplates } = useQuery({
    queryKey: ['recent-templates'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      
      const { data } = await supabase
        .from('templates')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5);
      
      return data || [];
    },
  });

  return (
    <div className="p-6 space-y-8">
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold">Welcome to DIYLegal{userName ? `, ${userName}` : ''}</h1>
        <p className="text-muted-foreground">Your intelligent legal document management system</p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Create, edit and manage your legal documents</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button 
              variant="outline" 
              className="h-32 flex flex-col gap-3 justify-center" 
              onClick={() => navigate('/templates')}
            >
              <FilePlus className="h-8 w-8 text-primary" />
              <span>Create New Document</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-32 flex flex-col gap-3 justify-center" 
              onClick={() => navigate('/documents')}
            >
              <FileText className="h-8 w-8 text-primary" />
              <span>My Documents</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-32 flex flex-col gap-3 justify-center" 
              onClick={() => navigate('/upload')}
            >
              <Upload className="h-8 w-8 text-primary" />
              <span>Upload Template</span>
            </Button>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="recent" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="recent">Recent Activity</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
        </TabsList>
        
        <TabsContent value="recent" className="space-y-4">
          {recentDocuments && recentDocuments.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentDocuments.slice(0, 3).map((doc) => (
                <Card key={doc.id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      {doc.filename}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {new Date(doc.updated_at || doc.created_at).toLocaleDateString()}
                    </CardDescription>
                  </CardHeader>
                  <CardFooter>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-primary"
                      onClick={() => navigate(`/documents/${doc.id}/edit`)}
                    >
                      <Pencil className="h-4 w-4 mr-2" /> Edit
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No recent activity found. Get started by creating a document.
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="templates" className="space-y-4">
          {recentTemplates && recentTemplates.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentTemplates.slice(0, 6).map((template) => (
                <Card key={template.id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      {template.filename}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {new Date(template.created_at).toLocaleDateString()}
                    </CardDescription>
                  </CardHeader>
                  <CardFooter>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-primary"
                      onClick={() => navigate(`/templates/${template.id}`)}
                    >
                      Create Document
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No templates found. Upload a template to get started.
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="documents" className="space-y-4">
          {recentDocuments && recentDocuments.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentDocuments.map((doc) => (
                <Card key={doc.id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      {doc.filename}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {new Date(doc.updated_at || doc.created_at).toLocaleDateString()}
                    </CardDescription>
                  </CardHeader>
                  <CardFooter>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-primary"
                      onClick={() => navigate(`/documents/${doc.id}/edit`)}
                    >
                      <Pencil className="h-4 w-4 mr-2" /> Edit
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No documents found. Create a document from a template to get started.
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Index;
